import Constants from 'expo-constants';

import type {
  CardData,
  IEProtectData,
  IEncryptionService,
  IGetWebviewPendingEncryption,
  OrbitalEncryptionResult,
  WorldpayEncryptionResult,
} from '..';
import { PaymentProcessor } from '..';

import { detectCardBrand } from './detect-card-brand-utils';
import type { EncryptionConfig } from './webview-encryption-service';

/**
 * Web-based encryption service, no WebView needed
 * On web, we dynamically load encryption scripts into the page
 * and call the encryption functions directly from the window object.
 */
export class WebEncryptionService implements IEncryptionService {
  private config: EncryptionConfig;
  private loadedScripts: Set<string> = new Set();

  constructor() {
    this.config = {
      orbital:
        Constants.expoConfig?.extra?.eas?.US?.orbital ||
        Constants.expoConfig?.extra?.eas?.CA?.orbital,
      worldpay: {
        ...(Constants.expoConfig?.extra?.eas?.US?.worldpay ||
          Constants.expoConfig?.extra?.eas?.CA?.worldpay),
        merchantReportGroup: `${Constants.expoConfig?.extra?.eas?.env}-FHS-${Constants.expoConfig?.extra?.eas?.buildRegion}`,
      },
    };
  }

  /** Worldpay/Orbital require digits-only; non-numeric input causes "Account number not numeric". */
  private static digitsOnly(s: string): string {
    return (s ?? '').replace(/\D/g, '');
  }

  /**
   * Encrypt card data using processor-specific encryption
   *
   * @param cardData - Card details to encrypt
   * @param processor - 'orbital' or 'worldpay'
   * @param orderId - Optional order ID for Worldpay
   */
  async encrypt(
    cardData: CardData,
    processor: PaymentProcessor,
    orderId?: string
  ): Promise<OrbitalEncryptionResult | WorldpayEncryptionResult> {
    const normalized: CardData = {
      ...cardData,
      cardNumber: WebEncryptionService.digitsOnly(cardData.cardNumber),
      cvv: WebEncryptionService.digitsOnly(cardData.cvv),
    };
    if (processor === PaymentProcessor.ORBITAL) {
      return this.encryptOrbital(normalized);
    } else {
      return this.encryptWorldpay(normalized, orderId);
    }
  }

  // Orbital (Safetech PIE) encryption - Web version
  private async encryptOrbital(cardData: CardData): Promise<OrbitalEncryptionResult> {
    const config = this.config?.orbital;
    if (!config) {
      throw new Error('Orbital encryption config not available');
    }

    const { baseUrl, pieFormat, subscriberId, mode } = config;
    const embed = mode === 'eFPE';

    // Construct script URLs from EAS
    const getKeyScriptUrl = `${baseUrl}/pie/v1/${pieFormat}${subscriberId}/getkey.js`;
    const encryptionScriptUrl = `${baseUrl}/pie/v1/encryption.js`;

    try {
      // Load Orbital PIE scripts into the page
      await this.loadScript(getKeyScriptUrl);
      await this.loadScript(encryptionScriptUrl);

      // Validate card number checksum
      if (!window.ValidatePANChecksum) {
        throw new Error('ValidatePANChecksum function not loaded');
      }

      const hasValidChecksum = window.ValidatePANChecksum(cardData.cardNumber);
      if (!hasValidChecksum) {
        throw new Error('PAN has invalid checksum');
      }

      // Encrypt card data using PIE
      if (!window.ProtectPANandCVV) {
        throw new Error('ProtectPANandCVV function not loaded');
      }

      const result = window.ProtectPANandCVV(cardData.cardNumber, cardData.cvv, !embed);
      // Correct result returns 3 element array
      if (!result || !Array.isArray(result) || result.length < 3) {
        throw new Error('Encryption failed - invalid result');
      }

      const [cryptCard, cryptCvv, integrityCheck] = result;

      // Check PIE metadata is available for assembling proper context
      if (!window.PIE) {
        throw new Error('PIE metadata not available');
      }

      // Return encryption result in our format
      return {
        encryptedCardNum: cryptCard,
        encryptedCvv: cryptCvv,
        pieFormat: pieFormat as 'FPE' | 'eFPE',
        pieMode: mode,
        piePhaseId: window.PIE.phase,
        pieIntegrityCheck: integrityCheck,
        pieSubscriberId: subscriberId,
        pieKeyID: window.PIE.key_id,
        cardBrand: detectCardBrand(cardData.cardNumber),
        bin: cardData.cardNumber.substring(0, 6),
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Orbital encryption error:', error);
      throw error;
    }
  }

  // Worldpay (eProtect) encryption - Web version
  private async encryptWorldpay(
    cardData: CardData,
    orderId?: string
  ): Promise<WorldpayEncryptionResult> {
    const config = this.config.worldpay;
    if (!config) {
      throw new Error('Worldpay encryption config not available');
    }

    const { eProtectUrl, paypageId } = config;
    const env = Constants.expoConfig?.extra?.eas?.env ?? '';
    const buildRegion = Constants.expoConfig?.extra?.eas?.buildRegion ?? '';
    const merchantReportGroup = `${env}-FHS-${buildRegion}`.toUpperCase();
    const scriptUrl = `${eProtectUrl}/eProtect/eProtect-api3.js`;

    try {
      // Load Worldpay eProtect script
      await this.loadScript(scriptUrl);

      const merchantOrderId = orderId?.slice(0, 20) || 'N/A';

      const preRequest = {
        paypageId,
        reportGroup: merchantReportGroup,
        orderId: merchantOrderId,
        id: merchantOrderId,
        url: eProtectUrl,
      };

      const formFields = {
        accountNum: {
          value: cardData.cardNumber,
        },
        cvv2: {
          value: cardData.cvv,
        },
      };

      const timeout = 20000; // 20 seconds

      // Return a promise that resolves with the encryption result
      return new Promise((resolve, reject) => {
        if (!window.eProtect) {
          reject(new Error('eProtect library not loaded'));
          return;
        }

        try {
          new window.eProtect().sendToEprotect(
            preRequest,
            formFields,
            (data: IEProtectData) => {
              // Success callback
              resolve({
                lowValueToken: data.paypageRegistrationId,
                type: data.type,
                bin: data.bin,
                last4: data.lastFour,
                expiryMonth: data.expMonth,
                expiryYear: data.expYear,
              });
            },
            (error: any) => {
              reject(new Error(error.message || 'eProtect encryption failed'));
            },
            () => {
              // Timeout callback
              reject(new Error('eProtect request timeout'));
            },
            timeout
          );
        } catch (error) {
          reject(error);
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Worldpay encryption error:', error);
      throw error;
    }
  }

  // Scripts are cached to avoid reloading
  private async loadScript(url: string): Promise<void> {
    // Don't reload if already loaded
    if (this.loadedScripts.has(url)) {
      return;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;

      script.onload = () => {
        this.loadedScripts.add(url);
        resolve();
      };

      script.onerror = () => {
        reject(new Error(`Failed to load script: ${url}`));
      };

      document.head.appendChild(script);
    });
  }

  // Not needed on web; encryption happens synchronously
  getWebviewPendingEncryption(): IGetWebviewPendingEncryption | null {
    return null;
  }

  clearPendingEncryption(): void {
    // Not needed on web
  }
}

// TODO: change global definition in legacy
// declare global {
//   interface Window {
//     // Orbital (Safetech PIE)
//     ProtectPANandCVV: (
//       cardNumber: string,
//       cvv: string,
//       useFPE: boolean
//     ) => [string, string, string] | null;
//     ValidatePANChecksum: (cardNumber: string) => boolean;
//     PIE: {
//       key_id: string;
//       phase: string;
//     };

//     // Worldpay (eProtect)
//     eProtect: new () => {
//       sendToEprotect(
//         preRequest: any,
//         formFields: any,
//         onSuccess: (data: any) => void,
//         onError: (error: any) => void,
//         onTimeout: () => void,
//         timeout: number
//       ): void;
//     };
//   }
// }
